class ProductVariantDefaultSelectorLayout extends HTMLElement {
  constructor() {
    super();
    this.variantButtonClass = ".product-variant-button-selector";
  }

  /**
   * Called when the element is inserted into the DOM.
   */
  connectedCallback() {
    this.section = this;
    this.section.addEventListener(
      "click",
      this.clickVariantEventHandler.bind(this)
    );

    this.loadSection();
  }

  /**
   * Called when the element is removed from the DOM.
   * Removes event listeners to prevent memory leaks.
   */
  disconnectedCallback() {
    this.section.removeEventListener(
      "click",
      this.clickVariantEventHandler.bind(this),
      true
    );
  }

  /**
   * Loads the section by filtering the product gallery based
   * on the selected variant.
   */
  loadSection() {
    const selectedVariant = this.section.querySelector(
      `[data-variant-selected="true"]`
    );
    const { variantHandle, variantIsSoldOut } = selectedVariant.dataset;
    Shopify?.ProductGalleryFilterByVariantHandle(variantHandle);
    Shopify?.ProductOverviewSetAddToCartDisabledAttribute(variantIsSoldOut);
    Shopify?.ProductOverviewShowOutStockKlavyoButton(variantIsSoldOut);
    this.notifyVariantChange(selectedVariant);
  }

  /**
   * Event handler for click events on variant buttons.
   * Filters the product gallery and updates the selected variant state.
   * @param {Event} event - The click event.
   */
  clickVariantEventHandler(event) {
    const variant = event.target.closest(".product-variant-button-selector");
    if (!variant) return;

    const { variantHandle, variantIsSoldOut } = variant.dataset;
    if (variantHandle)
      Shopify?.ProductGalleryFilterByVariantHandle(variantHandle);

    Shopify?.ProductOverviewSetAddToCartDisabledAttribute(variantIsSoldOut);
    Shopify?.ProductOverviewShowOutStockKlavyoButton(variantIsSoldOut);

    this.removeSelectedVariantState();
    this.setSelectedVariantState(variant);
    this.notifyVariantChange(variant);
  }

  /**
   * Removes the selected state from the previously selected variant button.
   */
  removeSelectedVariantState() {
    const selectedEl = this.section.querySelector(
      `${this.variantButtonClass}[data-variant-selected="true"]`
    );
    selectedEl.dataset.variantSelected = false;
  }

  /**
   * Sets the selected state for the specified variant button.
   * @param {HTMLElement} element - The variant button element to set as selected.
   */
  setSelectedVariantState(element) {
    element.dataset.variantSelected = true;
  }

  /**
   * Dispatches a custom event with the latest variant price so other components can react.
   * @param {HTMLElement} variant - The variant button element.
   */
  notifyVariantChange(variant) {
    const priceInCents = this.getNumericDatasetValue(
      variant.dataset.variantPrice
    );
    if (priceInCents === null) return;

    const compareAtPriceInCents = this.getNumericDatasetValue(
      variant.dataset.variantCompareAtPrice
    );

    const detail = {
      id: variant.dataset.variantId,
      handle: variant.dataset.variantHandle,
      price: priceInCents,
      isSoldOut: variant.dataset.variantIsSoldOut === "true",
      compareAtPrice: compareAtPriceInCents,
    };

    Shopify?.ProductOverviewUpdateVariantPrice({
      price: priceInCents / 100,
      compareAtPrice: this.convertCentsToDollars(compareAtPriceInCents),
    });
    document.dispatchEvent(
      new CustomEvent("pdp:variant-price-update", { detail })
    );
  }

  /**
   * Converts cents into dollars.
   * @param {number|null} cents
   * @returns {number|null}
   */
  convertCentsToDollars(cents) {
    if (typeof cents !== "number" || Number.isNaN(cents)) return null;
    return cents / 100;
  }

  /**
   * Parses a numeric dataset value, returning null for empty or invalid values.
   * @param {string|undefined} value
   * @returns {number|null}
   */
  getNumericDatasetValue(value) {
    if (value === undefined || value === null || value === "") return null;
    const parsedValue = Number(value);
    return Number.isNaN(parsedValue) ? null : parsedValue;
  }
}

customElements.define(
  "product-variant-default-selector-layout",
  ProductVariantDefaultSelectorLayout
);
